import React, { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { normaliseCustomConfig, FONT_OPTIONS } from './data';
import AbstractScene from './AbstractScene';
import ShapeRenderer from './ShapeRenderer';

// CustomCardScene — renders a salon-branded voucher card from a custom
// design config. Canvas-style: positioned elements (text / image /
// amount) drawn over a background.
//
// In edit mode the canvas behaves like a Canva surface:
//   - Drag empty space → pans the background photo (only when bgMode=photo)
//   - Wheel on empty space → zooms the background photo
//   - Click an element → selects it
//   - Drag element body → moves the element
//   - Drag the bottom-right handle → resizes
//   - Click the top-right X → deletes the element
//   - Double-click text → inline edit
//   - Right-click → context menu
//
// Right-click guards: drag/resize handlers only fire on button === 0
// (left mouse) so the contextmenu event reaches the menu handler.

const isDarkColor = (hex) => {
  if (!hex || typeof hex !== 'string' || hex[0] !== '#') return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) < 130;
};

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const isLeftButton = (e) => e.button === undefined || e.button === 0;

const buildBgStyle = (cfg) => {
  if (cfg.bgMode === 'gradient' && cfg.bgGradient) {
    const { from, to, angle } = cfg.bgGradient;
    return { background: `linear-gradient(${angle}deg, ${from}, ${to})` };
  }
  return { background: cfg.bgColor };
};

// Scale clamps per element type. Images get a high ceiling so they can be
// enlarged past the artboard edge (Canva-style) — the base image box is
// small (140×80 design px), so a portrait photo needs a big multiplier just
// to fill the card, let alone bleed off it.
const SCALE_LIMITS = {
  text:   { min: 0.4, max: 4 },
  image:  { min: 0.3, max: 16 },
  amount: { min: 0.5, max: 5 },
  shape:  { min: 0.3, max: 5 },
};

const CustomCardScene = ({
  design,
  value,
  currency = '£',
  promo = null,
  editable = false,
  selectedId = null,
  selectedIds = [],
  editingId = null,
  onSelect,
  onElementChange,
  onElementsChange,
  onElementDuplicate,
  onElementRemove,
  onElementDoubleClick,
  onElementContextMenu,
  onCommitEdit,
  onBgPhotoChange,
}) => {
  const rootRef = useRef(null);
  const editInputRef = useRef(null);
  // Ref to the currently-selected element's wrapper + a flag for whether the
  // floating action pill must flip below the element. In the designer the
  // card itself no longer clips (Canva-style), so the real clip boundary is
  // the grey workspace (.v2-cd-canvas-area). When the element sits too close
  // to the top of that boundary, the pill would be cut off, so we flip it
  // below instead.
  const selectedWrapRef = useRef(null);
  const floatmenuRef = useRef(null);
  const [pillBelow, setPillBelow] = useState(false);
  // Snap guides — when the dragged element's centre snaps to another
  // element's centre or the card centre, the matching axis records the
  // % coordinate so a thin guide line renders across the card.
  const [snapGuides, setSnapGuides] = useState({ x: null, y: null });

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // After layout, decide if the floating pill must flip below the selected
  // element. If the element sits too close to the artboard top (where the
  // pill would be clipped), render it below instead. Runs on selection +
  // position changes (drag re-renders update config, re-running this).
  //
  // The clearance needed is the pill's OWN rendered height, not a fixed
  // guess: the pill is inverse-scaled to stay a constant on-screen size, so
  // on a card displayed small (a bigger inverse-scale) it renders taller in
  // real pixels than on a card at full size. A fixed number sized for one
  // card size under-counted that on smaller mobile cards, so the pill kept
  // flagging itself as fitting above when it no longer did, and clipped
  // against the workspace top. getBoundingClientRect includes the scale
  // transform, and the pill's height is the same whether it is currently
  // rendered above or below, so this is safe to read either way.
  useLayoutEffect(() => {
    const measure = () => {
      const wrap = selectedWrapRef.current;
      if (!wrap) { setPillBelow(false); return; }
      // Measure against the real clip boundary. In the designer the card no
      // longer clips, so the workspace (.v2-cd-canvas-area) is what cuts off
      // the pill; fall back to the card for any non-designer mount.
      const clipper = wrap.closest('.v2-cd-canvas-area') || wrap.closest('.v2-card');
      if (!clipper) return;
      const wr = wrap.getBoundingClientRect();
      const cr = clipper.getBoundingClientRect();
      const pillHeight = floatmenuRef.current ? floatmenuRef.current.getBoundingClientRect().height : 38;
      setPillBelow((wr.top - cr.top) < pillHeight + 16);
    };
    measure();
    // Double-clicking text to edit it opens the on-screen keyboard, which on
    // mobile shrinks the visible viewport well after this effect's first
    // run (the keyboard animates in over ~250ms), leaving the initial
    // "fits above" decision stale for exactly the moment it matters most.
    // visualViewport's resize event is what fires for that; window resize
    // covers everything else (orientation change, browser chrome).
    const vv = window.visualViewport;
    window.addEventListener('resize', measure);
    if (vv) vv.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      if (vv) vv.removeEventListener('resize', measure);
    };
  });

  // Drag-to-reposition for an element. Includes snap-to-alignment:
  // when the moving element's centre lands within 1.6% of the card
  // centre or another element's centre on either axis, it snaps and
  // a thin pink guide line renders across the card.
  const startDrag = useCallback((id) => (e) => {
    if (!editable || !onElementChange || !rootRef.current) return;
    if (editingId === id) return;
    if (!isLeftButton(e)) return;  // let right-click reach contextmenu
    e.stopPropagation();
    e.preventDefault();

    const rect = rootRef.current.getBoundingClientRect();
    const startCX = e.clientX;
    const startCY = e.clientY;

    // Shift/Cmd-click toggles this element in the selection (no drag).
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      if (onSelect) onSelect(id, { additive: true });
      return;
    }

    // Work out what this drag moves: an existing multi-selection, or the
    // clicked element's group, or just the element. Reading the group from
    // the config means clicking a group and dragging moves the whole group on
    // the very first gesture, before selection state has updated.
    const cfgAtStart = normaliseCustomConfig(design);
    const elNow = cfgAtStart.elements.find(x => x.id === id);
    const groupMates = elNow?.groupId
      ? cfgAtStart.elements.filter(x => x.groupId === elNow.groupId).map(x => x.id)
      : [id];
    const inMulti = selectedIds.length > 1 && selectedIds.includes(id);
    const moveSet = inMulti ? selectedIds : groupMates;

    // Reflect the selection unless this element is already part of the current
    // multi-selection (handleSelect is group-aware, so this picks the group).
    if (!inMulti && onSelect) onSelect(id);

    // Move the whole set together (snapping off) when it's more than one.
    if (moveSet.length > 1 && onElementsChange) {
      const starts = {};
      moveSet.forEach((sid) => {
        const el = cfgAtStart.elements.find(x => x.id === sid);
        if (el) starts[sid] = { x: el.x ?? 50, y: el.y ?? 50 };
      });
      let moved = false;
      const onMoveMulti = (ev) => {
        moved = true;
        const dx = (ev.clientX - startCX) / rect.width * 100;
        const dy = (ev.clientY - startCY) / rect.height * 100;
        const patch = {};
        for (const sid in starts) patch[sid] = { x: starts[sid].x + dx, y: starts[sid].y + dy };
        onElementsChange(patch);
      };
      const onUpMulti = () => {
        // A click (no drag) collapses to this element (group-aware: re-selects
        // the whole group for a grouped element).
        if (!moved && onSelect) onSelect(id);
        window.removeEventListener('pointermove', onMoveMulti);
        window.removeEventListener('pointerup', onUpMulti);
      };
      window.addEventListener('pointermove', onMoveMulti);
      window.addEventListener('pointerup', onUpMulti);
      return;
    }

    const currentX = parseFloat(e.currentTarget.style.left) || 50;
    const currentY = parseFloat(e.currentTarget.style.top) || 50;

    // Snap targets — captured at drag start so they stay still while
    // this element moves. Card centre plus every OTHER element's
    // centre on each axis.
    const others = (cfgAtStart.elements || []).filter(el => el.id !== id);
    const snapTargetsX = [50, ...others.map(el => el.x).filter(v => Number.isFinite(v))];
    const snapTargetsY = [50, ...others.map(el => el.y).filter(v => Number.isFinite(v))];
    const SNAP = 1.6;  // % threshold

    const onMove = (ev) => {
      const dx = (ev.clientX - startCX) / rect.width * 100;
      const dy = (ev.clientY - startCY) / rect.height * 100;
      let newX = currentX + dx;
      let newY = currentY + dy;

      // Closest target within threshold wins per axis.
      let bestX = null, bestXDist = SNAP;
      for (const t of snapTargetsX) {
        const d = Math.abs(newX - t);
        if (d < bestXDist) { bestX = t; bestXDist = d; }
      }
      let bestY = null, bestYDist = SNAP;
      for (const t of snapTargetsY) {
        const d = Math.abs(newY - t);
        if (d < bestYDist) { bestY = t; bestYDist = d; }
      }
      if (bestX !== null) newX = bestX;
      if (bestY !== null) newY = bestY;

      setSnapGuides({ x: bestX, y: bestY });
      onElementChange(id, { x: newX, y: newY });
    };
    const onUp = () => {
      setSnapGuides({ x: null, y: null });
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [editable, onElementChange, onElementsChange, onSelect, editingId, design, selectedIds]);

  // Rotate-handle drag — orbit around the element's centre. The angle
  // between pointer and centre at drag-start is the baseline; the
  // delta is added to the element's existing rotation. Snaps to every
  // 45° (0, 45, 90, 135, 180, …) when within 4° for clean alignments.
  const startRotate = useCallback((id) => (e) => {
    if (!editable || !onElementChange) return;
    if (!isLeftButton(e)) return;
    e.stopPropagation();
    e.preventDefault();
    const wrapper = e.currentTarget.parentElement;
    const wrapperRect = wrapper.getBoundingClientRect();
    const cx = wrapperRect.left + wrapperRect.width / 2;
    const cy = wrapperRect.top + wrapperRect.height / 2;
    const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    const startRot = parseFloat(wrapper.getAttribute('data-rotation')) || 0;

    const onMove = (ev) => {
      const angle = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
      let next = startRot + (angle - startAngle);
      const normalised = ((next % 360) + 540) % 360 - 180;
      for (const snap of [-180, -135, -90, -45, 0, 45, 90, 135, 180]) {
        if (Math.abs(normalised - snap) < 4) { next = snap; break; }
      }
      onElementChange(id, { rotation: next });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [editable, onElementChange]);

  // Corner-handle drag-to-resize.
  const startResize = useCallback((id, type) => (e) => {
    if (!editable || !onElementChange) return;
    if (!isLeftButton(e)) return;
    e.stopPropagation();
    e.preventDefault();
    const wrapper = e.currentTarget.parentElement;
    const wrapperRect = wrapper.getBoundingClientRect();
    const cx = wrapperRect.left + wrapperRect.width / 2;
    const cy = wrapperRect.top + wrapperRect.height / 2;
    const startDist = Math.hypot(e.clientX - cx, e.clientY - cy) || 1;
    const startScale = parseFloat(wrapper.getAttribute('data-scale')) || 1;
    const { min, max } = SCALE_LIMITS[type] || SCALE_LIMITS.text;

    const onMove = (ev) => {
      const dist = Math.hypot(ev.clientX - cx, ev.clientY - cy);
      const ratio = dist / startDist;
      onElementChange(id, { scale: clamp(startScale * ratio, min, max) });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [editable, onElementChange]);

  // Side-handle drag-to-EXPAND the text box width (Canva-style). Unlike the
  // corner handles (which uniformly scale the whole element), the side
  // handles only change `el.width` — the text inside re-wraps to fit. The
  // element stays centred on (el.x, el.y), so width grows symmetrically
  // around the centre as the cursor moves (1 px cursor move = 2 px width
  // change). The drag is projected onto the element's local x-axis so it
  // still feels right for rotated elements. Width is stored in design
  // pixels (matches `--u`'s 720 reference) so it scales with card size.
  const startResizeWidth = useCallback((id, side) => (e) => {
    if (!editable || !onElementChange) return;
    if (!isLeftButton(e)) return;
    e.stopPropagation();
    e.preventDefault();
    const wrapper = e.currentTarget.parentElement;
    const wrapperRect = wrapper.getBoundingClientRect();
    const startWidthPx = wrapperRect.width / (parseFloat(wrapper.getAttribute('data-scale')) || 1);
    const startCX = e.clientX;
    const startCY = e.clientY;
    const rot = (parseFloat(wrapper.getAttribute('data-rotation')) || 0) * Math.PI / 180;
    const cos = Math.cos(rot), sin = Math.sin(rot);
    // Project screen delta onto element's local x-axis for rotated text.
    const sideSign = side === 'r' ? 1 : -1;
    // Convert screen px ↔ design px using the card's actual width and 720
    // reference. .v2-card is the nearest ancestor with `--u`.
    const cardEl = wrapper.closest('.v2-card');
    const cardWidth = cardEl ? cardEl.getBoundingClientRect().width : 720;
    const screenToDesign = 720 / cardWidth;
    const MIN = 40;  // design-px floor — keep handles grabbable
    const MAX = 1200;

    const onMove = (ev) => {
      const dxScreen = ev.clientX - startCX;
      const dyScreen = ev.clientY - startCY;
      const dxLocal = dxScreen * cos + dyScreen * sin;
      // Symmetric grow: cursor moves 1 → width grows 2 (both sides).
      const newWidthPx = startWidthPx + sideSign * dxLocal * 2;
      const newWidthDesign = newWidthPx * screenToDesign;
      onElementChange(id, { width: clamp(newWidthDesign, MIN, MAX) });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [editable, onElementChange]);

  const onContextMenu = useCallback((id) => (e) => {
    if (!editable || !onElementContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    if (onSelect) onSelect(id);
    onElementContextMenu(id, e.clientX, e.clientY);
  }, [editable, onElementContextMenu, onSelect]);

  const onDoubleClick = useCallback((id, type) => (e) => {
    if (!editable || !onElementDoubleClick || type !== 'text') return;
    e.stopPropagation();
    onElementDoubleClick(id);
  }, [editable, onElementDoubleClick]);

  if (!design) return null;
  const cfg = normaliseCustomConfig(design);
  const elements = cfg.elements || [];
  const isPhotoBg = cfg.bgMode === 'photo' && cfg.bgPhoto?.url;

  // Pan the background photo by dragging empty canvas. dx/dy → focalX/Y
  // in the inverse direction (drag right = reveal left, so focal moves
  // left). Only fires when bg is a photo and the user isn't dragging
  // an element. The element handlers stopPropagation so this won't run
  // while dragging an element.
  const startCanvasPan = (e) => {
    if (!editable || !onBgPhotoChange || !isPhotoBg) return;
    if (e.target !== e.currentTarget) return;  // skip if element was hit
    if (!isLeftButton(e)) return;
    e.preventDefault();
    const rect = rootRef.current.getBoundingClientRect();
    const startCX = e.clientX;
    const startCY = e.clientY;
    const startFX = cfg.bgPhoto.focalX ?? 50;
    const startFY = cfg.bgPhoto.focalY ?? 50;

    const onMove = (ev) => {
      const dx = (ev.clientX - startCX) / rect.width * 100;
      const dy = (ev.clientY - startCY) / rect.height * 100;
      onBgPhotoChange({
        focalX: clamp(startFX - dx, 0, 100),
        focalY: clamp(startFY - dy, 0, 100),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Wheel-to-zoom on the background photo. Wheel up = zoom in.
  const onCanvasWheel = (e) => {
    if (!editable || !onBgPhotoChange || !isPhotoBg) return;
    e.preventDefault();
    const currentZoom = cfg.bgPhoto.zoom ?? 1;
    // deltaY > 0 means wheel-down (zoom out). Scale by a fraction of
    // the current value for natural feel; cap range 1–3.
    const nextZoom = clamp(currentZoom - e.deltaY * 0.002, 1, 3);
    onBgPhotoChange({ zoom: nextZoom });
  };

  const cssVars = {
    '--cc-ink': cfg.ink,
    '--cc-accent': cfg.accent,
    ...buildBgStyle(cfg),
  };

  // ─── Background layers ───
  const patternBg = (cfg.bgMode === 'pattern')
    ? (
      <div className="v2-cc-pattern-bg" aria-hidden>
        <AbstractScene occasion={cfg.bgPattern?.occasion || 'birthday'} />
      </div>
    )
    : null;

  const photoBg = isPhotoBg
    ? (
      <img
        src={cfg.bgPhoto.url}
        alt=""
        className="v2-cc-photo-img"
        style={{
          objectPosition: `${cfg.bgPhoto.focalX ?? 50}% ${cfg.bgPhoto.focalY ?? 50}%`,
          transform: `scale(${cfg.bgPhoto.zoom ?? 1})`,
          transformOrigin: `${cfg.bgPhoto.focalX ?? 50}% ${cfg.bgPhoto.focalY ?? 50}%`,
        }}
        draggable={false}
      />
    )
    : null;

  const photoOverlay = isPhotoBg
    ? <div className="v2-cc-photo-overlay" aria-hidden />
    : null;

  const promoNode = promo != null
    ? <span className="v2-cc-promo">Pay {currency}{promo}</span>
    : null;

  // Photo manipulation hint — shown in edit mode + photo bg, fades out
  // after the salon starts interacting.
  const photoHint = (editable && isPhotoBg)
    ? (
      <div className="v2-cc-photo-hint" aria-hidden>
        Drag to pan · scroll to zoom
      </div>
    )
    : null;

  const renderElement = (el) => {
    const isSelected = selectedIds.includes(el.id);
    // "Primary" = the lone selected element. Per-element chrome (resize /
    // rotate handles, the floating action pill) only shows for a single
    // selection; in a multi-selection each member just gets an outline.
    const isPrimary = selectedIds.length === 1 && selectedIds[0] === el.id;
    const isEditing = editingId === el.id;
    const fontEntry = FONT_OPTIONS.find(f => f.id === (el.font || cfg.fontFamily)) || FONT_OPTIONS[0];
    // Inverse-scale the handles + X so they stay at fixed pixel size
    // regardless of the element's transform: scale(). A tiny floor on
    // el.scale prevents division explosions for unusually small values.
    const inverseScale = 1 / Math.max(0.1, el.scale ?? 1);

    // Per-element colour wins over card-level ink. Fallback to ink so
    // a fresh element reads against the card bg.
    const textColor = el.color || cfg.ink || '#FFFFFF';
    // Per-element fontSize is expressed in design pixels (matches the
    // 720-wide reference used by `--u` on .v2-card). Multiplying by --u
    // makes the type scale with card width — so an element designed at
    // 28 px stays visually identical when the card is rendered smaller
    // in the library or on mobile.
    const scaledFontSize = el.fontSize
      ? `calc(${el.fontSize} * var(--u, 1px))`
      : undefined;
    const textStyle = {
      fontFamily: fontEntry.family,
      color: textColor,
      fontWeight: el.bold ? 700 : 500,
      fontStyle: el.italic ? 'italic' : 'normal',
      textDecoration: el.underline ? 'underline' : 'none',
      textAlign: el.align || 'center',
      fontSize: scaledFontSize,
      // Optional soft drop shadow (off unless the element sets it)
      textShadow: el.shadow
        ? `calc(2.5 * var(--u, 1px)) calc(2.5 * var(--u, 1px)) 0 ${el.shadowColor || 'rgba(26,26,30,0.3)'}`
        : undefined,
      ...(el.badge === 'pill' ? {
        background: el.badgeColor || cfg.accent || '#E8A838',
        borderRadius: 999,
        padding: 'calc(6 * var(--u, 1px)) calc(18 * var(--u, 1px))',
      } : {}),
    };

    let inner;
    if (el.type === 'amount') {
      inner = (
        <span className="v2-cc-amount" style={{
          fontFamily: fontEntry.family,
          color: textColor,
          fontSize: scaledFontSize,
        }}>
          <span className="v2-cc-amount-cur">{currency}</span>
          <span className="v2-cc-amount-num">{value ?? 50}</span>
        </span>
      );
    } else if (el.type === 'text') {
      if (isEditing) {
        inner = (
          <input
            ref={editInputRef}
            type="text"
            className="v2-cc-text-input"
            style={textStyle}
            value={el.content ?? ''}
            onChange={(e) => onElementChange(el.id, { content: e.target.value })}
            onBlur={() => onCommitEdit && onCommitEdit(el.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); onCommitEdit && onCommitEdit(el.id); }
              if (e.key === 'Escape') { onCommitEdit && onCommitEdit(el.id); }
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          />
        );
      } else {
        inner = (
          <span
            className={`v2-cc-text ${!el.content ? 'is-empty' : ''}`}
            style={textStyle}
          >
            {el.content || 'Double-click to edit'}
          </span>
        );
      }
    } else if (el.type === 'image' && el.src) {
      inner = <img src={el.src} alt="" className="v2-cc-img" draggable={false} />;
    } else if (el.type === 'shape') {
      inner = (
        <ShapeRenderer
          shape={el.shape || 'rect'}
          fill={el.fill || cfg.ink || '#FFFFFF'}
          stroke={el.stroke || null}
          strokeWidth={el.strokeWidth || 0}
          radius={el.radius || 0}
        />
      );
    } else {
      return null;
    }

    // When a text element has been width-resized (Canva-style box
    // expansion), pin the wrapper to that width so the inner text wraps.
    // Width is design-pixel based so it scales with the card.
    const hasExplicitWidth = el.type === 'text' && el.width;
    const widthStyle = hasExplicitWidth
      ? { width: `calc(${el.width} * var(--u, 1px))` }
      : {};

    return (
      <div
        key={el.id}
        ref={isPrimary ? selectedWrapRef : undefined}
        data-el-id={el.id}
        className={[
          'v2-cc-el',
          `v2-cc-el-${el.type}`,
          editable ? 'is-editable' : '',
          isSelected ? 'is-selected' : '',
          isSelected && !isPrimary ? 'is-multi' : '',
          isEditing ? 'is-editing' : '',
          hasExplicitWidth ? 'is-width-set' : '',
        ].join(' ').trim()}
        style={{
          left: `${el.x}%`,
          top: `${el.y}%`,
          transform: `translate(-50%, -50%) rotate(${el.rotation ?? 0}deg) scale(${el.scale ?? 1})`,
          ...widthStyle,
        }}
        data-scale={el.scale ?? 1}
        data-rotation={el.rotation ?? 0}
        onPointerDown={editable ? startDrag(el.id) : undefined}
        onDoubleClick={editable ? onDoubleClick(el.id, el.type) : undefined}
        onContextMenu={editable ? onContextMenu(el.id) : undefined}
      >
        {inner}
        {editable && isPrimary && !isEditing && (
          <>
            {/* Four corner resize handles, Figma-style squares.
                Inverse-scaled so they stay constant pixel size. */}
            {['tl','tr','bl','br'].map((corner) => (
              <span
                key={corner}
                className={`v2-cc-resize-handle v2-cc-handle-${corner}`}
                style={{ transform: `scale(${inverseScale})` }}
                onPointerDown={startResize(el.id, el.type)}
                aria-label={`Resize ${corner}`}
                role="button"
              />
            ))}
            {/* Side handles — Canva-style left/right edge grabs that
                EXPAND the text box width rather than scale the whole
                element. Only on text (images keep aspect ratio; shapes
                and amount don't benefit from re-wrapping). Pill-shape
                so they visually distinguish from the corner squares. */}
            {el.type === 'text' && ['l','r'].map((side) => (
              <span
                key={side}
                className={`v2-cc-resize-handle v2-cc-handle-${side}`}
                style={{ transform: `translateY(-50%) scale(${inverseScale})` }}
                onPointerDown={startResizeWidth(el.id, side)}
                aria-label={`Expand ${side === 'l' ? 'left' : 'right'}`}
                role="button"
              />
            ))}
            {/* Rotate handle — circular knob ~22 px below the bottom-
                centre of the selection. Inverse-scaled. */}
            <button
              type="button"
              className="v2-cc-rotate-handle"
              style={{ transform: `translateX(-50%) scale(${inverseScale})` }}
              onPointerDown={startRotate(el.id)}
              onClick={(e) => e.stopPropagation()}
              aria-label="Rotate element"
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M2 7a5 5 0 1 1 1.5 3.5" />
                <path d="M1.5 11V8.5h2.5" />
              </svg>
            </button>
            {/* Floating Canva-style action menu above the selection.
                Inverse-scaled AND counter-rotated so it stays
                visually upright regardless of element rotation, but
                still moves with the element's top-centre. */}
            <div
              ref={floatmenuRef}
              className={`v2-cc-floatmenu ${pillBelow ? 'v2-cc-floatmenu--below' : ''}`}
              style={{ transform: `translate(-50%, ${pillBelow ? '12px' : '-100%'}) rotate(${-(el.rotation ?? 0)}deg) scale(${inverseScale})` }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {/* Layers — opens the reorder menu (bring forward / to front /
                  send backward / to back) at the click point. Visible
                  affordance so depth control is discoverable, not just on
                  right-click. */}
              <button
                type="button"
                className="v2-cc-floatmenu-btn"
                onClick={(e) => { e.stopPropagation(); onElementContextMenu && onElementContextMenu(el.id, e.clientX, e.clientY); }}
                aria-label="Layer order"
                title="Layer order"
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M8 2l5.5 3L8 8 2.5 5 8 2z" />
                  <path d="M2.5 8L8 11l5.5-3" />
                  <path d="M2.5 11L8 14l5.5-3" />
                </svg>
              </button>
              <button
                type="button"
                className="v2-cc-floatmenu-btn"
                onClick={(e) => { e.stopPropagation(); onElementDuplicate && onElementDuplicate(el.id); }}
                aria-label="Duplicate"
                title="Duplicate"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <rect x="5" y="5" width="9" height="9" rx="1.5" />
                  <path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" />
                </svg>
              </button>
              <button
                type="button"
                className="v2-cc-floatmenu-btn v2-cc-floatmenu-btn--danger"
                onClick={(e) => { e.stopPropagation(); onElementRemove && onElementRemove(el.id); }}
                aria-label="Delete"
                title="Delete"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M4 4l1 9a1 1 0 001 1h4a1 1 0 001-1l1-9" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Click empty canvas:
  //   - if photo bg, start panning
  //   - if not photo bg (or didn't drag), clear the selection
  const onRootPointerDown = (e) => {
    if (!editable) return;
    if (e.target !== e.currentTarget) return;
    if (isPhotoBg && isLeftButton(e)) {
      startCanvasPan(e);
    } else if (onSelect && isLeftButton(e)) {
      onSelect(null);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`v2-cc ${editable ? 'is-editable' : ''} ${isPhotoBg && editable ? 'is-photo-pannable' : ''}`}
      style={cssVars}
      onPointerDown={onRootPointerDown}
      onWheel={editable && isPhotoBg ? onCanvasWheel : undefined}
    >
      {/* Background-clipping layer — wraps photo / pattern / photo
          overlay so they're clipped to the card's rounded shape even
          when .v2-cc has overflow:visible (so elements can extend
          past the card edge in edit mode). */}
      <div className="v2-cc-bg-layer" aria-hidden>
        {patternBg}
        {photoBg}
        {photoOverlay}
      </div>
      {photoHint}
      {promoNode}
      {elements.map(renderElement)}
      {/* Artboard scrim (edit mode) — a card-shaped hole punched out of a
          full-bleed wash (via a huge box-shadow spread) so anything bleeding
          past the card edge fades toward the workspace colour. This shows the
          salon exactly what will be cropped off the final voucher. The wash
          matches the workspace, so empty space is unaffected; it's purely
          visual (pointer-events: none) so dragging/resizing still works. */}
      {editable && <div className="v2-cc-artboard-scrim" aria-hidden />}
      {/* Snap guide lines — only render while dragging snaps. */}
      {editable && snapGuides.x !== null && (
        <div className="v2-cc-guide v2-cc-guide-v" style={{ left: `${snapGuides.x}%` }} aria-hidden />
      )}
      {editable && snapGuides.y !== null && (
        <div className="v2-cc-guide v2-cc-guide-h" style={{ top: `${snapGuides.y}%` }} aria-hidden />
      )}
    </div>
  );
};

export { isDarkColor };
export default CustomCardScene;
